import React from 'react'
import { BasicDialogBody, Dialog } from '../../legacy-design-system/modal'
import { useIntl } from 'react-intl'
import { BlockIpForm } from './block-ip-form'
import { FormValidator } from '../../legacy-design-system/form'
import { useAddIpToBlockList } from './api/use-add-ip-to-block-list'
import { logError, logSuccess } from '../toaster'
import { DateOption } from '../../legacy-design-system/date-picker/shared'
import { connector } from '../../api/connector'
import { checkIpName } from './api/requests/check-ip-name'

export const blockIpDialogDataTestID = 'block-ip-dialog'

interface BlockIpDialogProps {
  organizationUuid: string
  ip?: string
  isOpen: boolean
  closeDialog: () => void
}

export function BlockIpDialog({
  organizationUuid,
  isOpen,
  ip: initialIP,
  closeDialog
}: BlockIpDialogProps) {
  const defaultName = ''
  const defaultIp = initialIP || ''
  const defaultExpiration = 1 as DateOption
  const defaultIsUniqueName = true
  const defaultIpMinRange = undefined
  const defaultIpMaxRange = undefined
  const defaultExpirationTime = undefined

  const [name, setName] = React.useState(defaultName)
  const [ip, setIp] = React.useState(defaultIp)
  const [expiration, setExpiration] = React.useState(defaultExpiration)
  const [isUniqueName, setIsUniqueName] = React.useState(defaultIsUniqueName)
  const [ipMinRange, setIpMinRange] = React.useState<string | undefined>(
    defaultIpMinRange
  )
  const [ipMaxRange, setIpMaxRange] = React.useState<string | undefined>(
    defaultIpMaxRange
  )
  const [expirationTime, setExpirationTime] = React.useState<Date | undefined>(
    defaultExpirationTime
  )

  const { formatMessage } = useIntl()

  function setDefaultState() {
    setName(defaultName)
    setIp(defaultIp)
    setExpiration(defaultExpiration)
    setIsUniqueName(defaultIsUniqueName)
    setIpMinRange(defaultIpMinRange)
    setIpMaxRange(defaultIpMaxRange)
    setExpirationTime(defaultExpirationTime)
  }

  function dismissDialog() {
    setDefaultState()
    closeDialog()
  }

  const addIpToBlockListMutation = useAddIpToBlockList({
    onSuccess: () => {
      logSuccess(
        formatMessage({
          id: 'detail_ipAddedToBlockListSuccessMsg'
        })
      )
      dismissDialog()
    },
    onError: () => {
      logError(
        formatMessage({
          id: 'detail_ipAddedToBlockListFailureMsg'
        })
      )
    }
  })

  const validateUniqueIpName = async (ipPolicyName: string) => {
    if (ipPolicyName && ipPolicyName.length > 0) {
      try {
        await checkIpName.call(
          { connector },
          {
            organizationUuid,
            name: ipPolicyName
          }
        )

        setIsUniqueName(true)
      } catch (error) {
        setIsUniqueName(false)
      }
    } else {
      setIsUniqueName(true)
    }
  }

  return (
    <Dialog
      testId={blockIpDialogDataTestID}
      isOpen={isOpen}
      size="large"
      renderTitle={() => formatMessage({ id: 'title_addIpToBlockList' })}
      onCancel={dismissDialog}
    >
      <FormValidator
        value={{
          name,
          ip
        }}
      >
        {({
          formIsValid,
          getFormFieldProps,
          setCustomValidity,
          resetValidationState
        }) => {
          return (
            <BasicDialogBody
              body={
                <BlockIpForm
                  name={name}
                  ip={ip}
                  expiration={expiration}
                  expirationTime={expirationTime}
                  isUniqueName={isUniqueName}
                  ipMinRange={ipMinRange}
                  ipMaxRange={ipMaxRange}
                  setName={setName}
                  setIp={setIp}
                  setExpiration={setExpiration}
                  setExpirationTime={setExpirationTime}
                  setIsUniqueName={setIsUniqueName}
                  setIpMinRange={setIpMinRange}
                  setIpMaxRange={setIpMaxRange}
                  getFormFieldProps={getFormFieldProps}
                  setCustomValidity={setCustomValidity}
                  validateUniqueIpName={validateUniqueIpName}
                />
              }
              onConfirm={() => {
                addIpToBlockListMutation.mutate({
                  organizationUuid,
                  ip,
                  name,
                  expiration,
                  expirationTime,
                  trusted: false
                })
                resetValidationState()
              }}
              onCancel={dismissDialog}
              confirmButtonText={formatMessage({ id: 'action_save' })}
              cancelButtonText={formatMessage({ id: 'action_cancel' })}
              disableConfirmButton={!formIsValid}
            />
          )
        }}
      </FormValidator>
    </Dialog>
  )
}
